import { useCallback, useState } from 'react'
import { useAsync } from 'react-use'
import { NyuDataSet, NyuGraphData } from '../../../shared/types'
import { getChunkUrl, isCustomOption } from '../utils/helpers'
import useGlobalState from './useGlobalState'

type TraverseObjectPathResult = {
    graphData: NyuGraphData
    path: string[]
    pathSegmentsOptions: string[][]
}

type DataSetRecord = Record<string, NyuDataSet>

function findDefaultPathSegment(parentObject: DataSetRecord): string {
    const [firstKey] = Object.keys(parentObject).toSorted((a, b) => {
        const [, isDefaultA] = isCustomOption(a)
        const [, isDefaultB] = isCustomOption(b)

        if (isDefaultA === isDefaultB) {
            return 0
        } else if (isDefaultA) {
            return -1
        } else {
            return 1
        }
    })
    return firstKey
}

type TraverseDataSetPathParams = {
    dataUrl: string
    object: NyuDataSet | NyuGraphData
    uncheckedPath: string[]
}

async function includeLink(
    link: string,
    params: TraverseDataSetPathParams,
    path: string[],
    pathSegmentsOptions: string[][]
) {
    const isAbsoluteUrl =
        link.startsWith('https://') || link.startsWith('http://')

    const url = isAbsoluteUrl ? link : getChunkUrl(params.dataUrl, link)

    const response = await fetch(url)
    const data = await response.json()

    if (isAbsoluteUrl) {
        const nextParams = {
            ...params,
            object: data.dataset,
            dataUrl: url, // overwrite the dataUrl so relative includes in the new dataset work
        }
        return await traverseDataSetPath(nextParams, [], [])
    } else {
        const nextParams = {
            ...params,
            object: data,
        }
        return await traverseDataSetPath(nextParams, path, pathSegmentsOptions)
    }
}

function objectIsGraphData(object: NyuDataSet | NyuGraphData): boolean {
    const keys = Object.keys(object)
    const expectedKeys = ['dataProperties', 'seriesProperties', 'data']
    const hasExpectedKeys = expectedKeys.every((key) => keys.includes(key))
    return hasExpectedKeys
}

function filterMarkers(s: string = ''): string {
    const [, , optionText] = isCustomOption(s)
    return optionText
}

export async function traverseDataSetPath(
    params: TraverseDataSetPathParams,
    path: string[] = [],
    pathSegmentsOptions: string[][] = []
): Promise<TraverseObjectPathResult> {
    const { object, uncheckedPath } = params

    if (objectIsGraphData(object)) {
        const graphData = object as NyuGraphData
        return {
            path,
            pathSegmentsOptions,
            graphData,
        }
    }

    const [uncheckedSegment] = uncheckedPath.slice(path.length, path.length + 1)
    const currentSegmentOptions = Object.keys(object)
    const defaultPathSegment = findDefaultPathSegment(object as DataSetRecord)
    const segmentIndex = currentSegmentOptions
        .map(filterMarkers)
        .indexOf(filterMarkers(uncheckedSegment))
    const segment =
        segmentIndex !== -1
            ? currentSegmentOptions[segmentIndex]
            : defaultPathSegment

    const nextPath = [...path, segment]
    const nextPathSegmentsOptions = [
        ...pathSegmentsOptions,
        currentSegmentOptions,
    ]

    const objectOrLink = (object as DataSetRecord)[segment]
    const objectIsLink = typeof objectOrLink === 'string'
    if (objectIsLink) {
        return await includeLink(
            objectOrLink,
            params,
            nextPath,
            nextPathSegmentsOptions
        )
    } else {
        const nextParams = {
            ...params,
            object: objectOrLink,
        }
        return await traverseDataSetPath(
            nextParams,
            nextPath,
            nextPathSegmentsOptions
        )
    }
}

export default function useTraverseDataSet(dataset: NyuDataSet) {
    const dataUrl = useGlobalState((state) => state.dataUrl)

    const [uncheckedPath, setUncheckedPath] = useState<string[]>([])

    const graphDataResult = useAsync(
        async () =>
            traverseDataSetPath({
                dataUrl,
                uncheckedPath,
                object: dataset,
            }),
        [dataUrl, uncheckedPath, dataset]
    )

    const pathResult: TraverseObjectPathResult | undefined =
        graphDataResult.loading ? undefined : graphDataResult.value

    const setPathSegment = useCallback(
        (index: number, value: string) => {
            const nextPath = [...(pathResult?.path || [])]
            nextPath[index] = value
            setUncheckedPath(nextPath)
        },
        [pathResult?.path, setUncheckedPath]
    )

    return {
        ...pathResult,
        setPathSegment,
    }
}
